import { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import RehypeRaw from "rehype-raw";

import ClickableDots from "sj-ui-toolkit/src/atlas/components/ClickableDots";

import { useInteraction } from "../contexts/SandboxHooks";
import { addClassNameToPre, remarkVimeo } from "../helpers/markdown";
import CodeBlock from "./CodeBlock";

import S from "../_styles.module.scss";

// Height of the tabs + the dots
const InstructionNavHeight = 90;

const Instructions = ({ steps }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const stepRefs = useRef([]);
  const isScrollingRef = useRef(false);

  const { reportStepsCompleted } = useInteraction();

  // Call reportStepsCompleted any time current step changes.
  useEffect(() => {
    reportStepsCompleted(currentStepIndex);
  }, [currentStepIndex, reportStepsCompleted]);

  const getDotState = index => {
    if (index < currentStepIndex) return "complete";
    if (index === currentStepIndex) return "in_progress";
    return "to_do";
  };

  const navigateToStep = useCallback(
    index => {
      if (index === currentStepIndex) return;
      const scroller = document.querySelector("#lesson-left-panel-scroll");
      const stepRef = stepRefs.current[index];
      if (!scroller || !stepRef) return;

      isScrollingRef.current = true;
      const scrollTop = stepRef.offsetTop - InstructionNavHeight;
      scroller.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });

      // Calculate a timeout based on # of steps to scroll
      // Using log based on observed behavior of scrollTo in Chrome
      const timeout = 500 + Math.log(Math.abs(currentStepIndex - index)) * 400;

      // Update current step index
      setCurrentStepIndex(index);

      // Allow some time for scrolling to finish before re-enabling scroll tracking
      setTimeout(() => {
        isScrollingRef.current = false;
      }, timeout);
    },
    [currentStepIndex]
  );

  // Track scroll position to update current step
  useEffect(() => {
    const scroller = document.querySelector("#lesson-left-panel-scroll");
    if (!scroller) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      // Find out which step we've scrolled to
      let furthestStepIndex;

      // If we have scrolled as much as possible, set to last step
      if (scroller.scrollHeight - scroller.scrollTop === scroller.clientHeight) {
        furthestStepIndex = steps.length - 1;
      }
      // Otherwise, figure out which step is the last one above the scroll position
      else {
        furthestStepIndex = 0;

        const scrollTop = scroller.scrollTop + InstructionNavHeight;

        stepRefs.current.forEach((stepRef, index) => {
          if (!stepRef) return;

          // A little bit of wiggle room
          if (scrollTop >= stepRef.offsetTop - 3) {
            furthestStepIndex = index;
          }
        });
      }

      setCurrentStepIndex(furthestStepIndex);
    };

    scroller.addEventListener("scroll", handleScroll);

    return () => {
      scroller.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className={S.instructionsContainer}>
      <div className={S.instructionsNavigation}>
        <div className="flex items-center gap-0.5">
          {steps.map((step, index) => (
            <ClickableDots
              key={index}
              title={`Step ${index + 1}`}
              dotState={getDotState(index)}
              onClick={() => navigateToStep(index)}
            />
          ))}
        </div>
      </div>
      {steps.map((step, index) => (
        <div key={index} className={S.instructionStep} ref={el => (stepRefs.current[index] = el)}>
          <ReactMarkdown
            remarkPlugins={[remarkVimeo]}
            rehypePlugins={[addClassNameToPre, RehypeRaw]}
            components={{
              h2: h2 => <h2 className="atlas-h4">{h2.children}</h2>,
              pre: pre => {
                return <CodeBlock node={pre.node}>{pre.children}</CodeBlock>;
              },
              code: code => <code className="atlas-code">{code.children}</code>,
            }}
          >
            {step.content.markdown}
          </ReactMarkdown>
        </div>
      ))}
    </div>
  );
};

export default Instructions;
